/**
 * Qué filas muestra la lista de la bandeja (y la de En vivo): filtros, vistas
 * y buscador. Lógica pura, sin React, para que se pueda correr en Node
 * (`tests/unit/portal_ts.py`).
 *
 * POR QUÉ SALIÓ DEL COMPONENTE
 * ----------------------------
 * QA de TEST, 05-oct-2026 (DEF-047): «Marcar resuelta» hacía desaparecer la
 * conversación de la bandeja —no estaba en ningún filtro y el buscador no la
 * encontraba por el nombre del cliente— mientras el cliente seguía
 * escribiendo. El backend ahora devuelve aparte las que resolvió quien mira
 * (`/conversations?scope=mine&resueltas=1`), y la lista tiene que decidir
 * dónde se ven. Esa decisión vivía dentro de un `useMemo` del componente,
 * donde solo se podía fijar con un `in` sobre la fuente: pasa igual con la
 * condición al revés.
 *
 * DOS REGLAS
 * ----------
 * - **Cada conversación vive en UNA vista**: «En curso» (viva y la conduce
 *   alguien), «Completos» (viva y completa por el agente), «Históricas» (sin
 *   actividad hace más de 24 h, o ya en el precalificador del socio) y
 *   «Resueltas» (cerradas por quien mira en los últimos `DIAS_RESUELTAS`
 *   días). Los filtros de atributo —«Pendientes»,
 *   «Documentos», «Sin asignar»— recortan dentro de «En curso», como siempre.
 * - **Buscar es encontrar**: con texto en el buscador la lista recorre TODAS
 *   las vistas, sea cual sea el filtro elegido. La pregunta del que escribe un
 *   nombre es «¿dónde está?», y una conversación escondida detrás de una vista
 *   es exactamente lo que QA no pudo encontrar. Cada fila lleva su chip
 *   («Resuelta», «Completo por IA», el estado), así que se sabe dónde vive.
 */

import { completoPorIA } from './atencion'
import { sinCanalConElCliente } from './sesionSocio'
import type { ConversationSummary } from './types/portal'

/** Cuánto sigue a la vista lo que uno resolvió. Es el del backend
 *  (`portal_api/operacion/resueltas.DIAS_RESUELTAS`), cruzado por test. */
export const DIAS_RESUELTAS = 30

export type InboxFilter =
  | 'todas' | 'pendientes' | 'documentos' | 'sin_asignar' | 'completos' | 'historicas'
  | 'resueltas'

/** Las vistas: dónde VIVE una conversación. Los demás filtros son atributos. */
export type Vista = 'todas' | 'completos' | 'historicas' | 'resueltas'

type Fila = Pick<ConversationSummary,
  'conversation_id' | 'contact_name' | 'phone' | 'last_message' | 'status' | 'assigned_to'
  | 'docs_pending_review' | 'docs_processing'>
  & Partial<Pick<ConversationSummary,
    'activity' | 'expediente' | 'conversation_state' | 'bot_interino'
    | 'reabierta_ts' | 'asesor_atendio_ts' | 'sesion_socio'>>

/** Sin mayúsculas ni tildes: «maria» encuentra a «María». */
function normalizar(texto: string): string {
  return (texto || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}

/**
 * ¿La fila coincide con lo buscado? Nombre, teléfono y último mensaje; en
 * monitoreo también el asesor, porque «quién está atendiendo a X» es la
 * pregunta que trae al supervisor a esa pantalla.
 */
export function coincide(c: Fila, busqueda: string, esMonitor = false): boolean {
  const q = normalizar(busqueda.trim())
  if (!q) return true
  return (
    normalizar(c.contact_name).includes(q)
    || (c.phone || '').includes(q)
    || normalizar(c.last_message).includes(q)
    || (esMonitor && normalizar(c.assigned_to ?? '').includes(q))
  )
}

/**
 * ¿Ya no se puede atender? Sin actividad hace más de 24 h, o la sesión volvió
 * al precalificador de un socio dueño de otro número.
 *
 * La segunda es regla de Ruben del 06-oct-2026: una conversación de Software
 * que volvió a su precalificador —a las 48 h del pase, o por cualquier otra
 * causa— tuvo actividad reciente y se veía «En curso», en la bandeja y en En
 * vivo, aunque nadie le puede escribir al cliente. La completa por el agente
 * sigue en «Completos»: ahí lo que queda es revisar el expediente, no el chat.
 */
export function esHistorica(c: Fila): boolean {
  if (!(c.activity?.is_live ?? true)) return true
  return sinCanalConElCliente(c) && !completoPorIA(c)
}

/** En qué vista vive. `resuelta`: vino en la lista de resueltas. */
export function vistaDe(c: Fila, resuelta = false): Vista {
  if (resuelta) return 'resueltas'
  if (esHistorica(c)) return 'historicas'
  return completoPorIA(c) ? 'completos' : 'todas'
}

/**
 * ¿El filtro elegido muestra esa vista? Los de atributo son recortes de
 * «En curso»: muestran su vista aunque el recorte deje afuera la fila.
 */
export function filtroMuestraLaVista(filtro: InboxFilter, vista: Vista): boolean {
  if (filtro === vista) return true
  return vista === 'todas'
    && (filtro === 'pendientes' || filtro === 'documentos' || filtro === 'sin_asignar')
}

/**
 * Las filas que se ven, en orden: primero las asignadas, después las
 * resueltas. Ver las dos reglas arriba.
 */
export function filasVisibles<T extends Fila>(p: {
  conversaciones: readonly T[]
  resueltas?: readonly T[]
  filtro: InboxFilter
  busqueda: string
  esMonitor?: boolean
}): T[] {
  const resueltas = p.resueltas ?? []
  const q = p.busqueda.trim()
  if (q) {
    // Buscar es encontrar: todas las vistas, sin recorte de filtro.
    return [...p.conversaciones, ...resueltas].filter((c) => coincide(c, q, p.esMonitor))
  }
  if (p.filtro === 'resueltas') return [...resueltas]
  const filtro = p.filtro
  return p.conversaciones.filter((c) => {
    // Las históricas (sin actividad hace más de 24 h) salen de TODAS las
    // vistas salvo la suya. En TEST había 5 pruebas abandonadas de entre 2 y
    // 8 días mezcladas con 2 chats reales, todas con `status` ACTIVE: la
    // pantalla decía "7 conversaciones del equipo" y solo 2 se estaban
    // atendiendo. El `status` no distingue una cosa de la otra porque se
    // queda como está para siempre si nadie cierra la conversación.
    const viva = !esHistorica(c)
    if (filtro === 'historicas') {
      if (viva) return false
    } else if (!viva) {
      return false
    } else if ((filtro === 'completos') !== completoPorIA(c)) {
      // «Completos» muestra solo esas; los demás filtros, todo menos esas.
      return false
    }
    if (filtro === 'pendientes' && c.status === 'ACTIVE') return false
    if (filtro === 'sin_asignar' && c.assigned_to) return false
    if (filtro === 'documentos' && c.docs_pending_review + c.docs_processing === 0) return false
    return true
  })
}
